import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  IconButton,
  Tooltip,
  TablePagination,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Build as BuildIcon,
  Timer as TimerIcon,
  WhatsApp as WhatsAppIcon,
  Check as CheckIcon,
  FilterAlt as FilterIcon,
  Clear as ClearIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import api from '../../utils/api';
import { openWhatsAppWithTemplate, TEMPLATE_TYPES, openMaintenanceReviewWhatsApp, openWhatsAppChat } from '../../utils/whatsappTemplates';
import MaintenanceNoteBell from './MaintenanceNoteBell';
import CompleteMaintenanceDialog from './CompleteMaintenanceDialog';
import logger from '../../utils/logger';

// Status color mapping
const statusColors = {
  'In Coda': 'default',
  'In Corso': 'primary',
  'Attesa Ricambi': 'warning',
  'Attesa Cliente': 'info',
  'Da Chiamare': 'secondary',
  'Da Ritirare': 'success',
  'Sollecitato': 'error',
  'Parcheggio': 'default',
  'Consegnato': 'success'
};

// Priority color mapping
const getPriorityColor = (priority) => {
  const priorityColors = {
    'Bassa': 'default',
    'Normale': 'info',
    'Alta': 'warning',
    'Urgente': 'error'
  };
  return priorityColors[priority] || 'default';
};

// Map maintenance status to appropriate WhatsApp template type
const statusToTemplateType = {
  // Stati senza messaggio (testo vuoto)
  'In Coda': null,
  'In Corso': null,
  'Attesa Ricambi': null,
  // Messaggi standard
  'Attesa Cliente': TEMPLATE_TYPES.ESTIMATE_READY, // "preventivo"
  'Da Chiamare': TEMPLATE_TYPES.MAINTENANCE_COMPLETION, // "completamento"
  'Da Ritirare': TEMPLATE_TYPES.PICKUP_REMINDER, // "sollecito"
  'Sollecitato': TEMPLATE_TYPES.PICKUP_REMINDER, // "sollecito"
  'Parcheggio': TEMPLATE_TYPES.PICKUP_REMINDER, // "sollecito"
  'Consegnato': TEMPLATE_TYPES.MAINTENANCE_REVIEW // "recensione"
};

const MaintenanceList = ({ maintenances, onViewDetails, onStartRepair, loading, type }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [activeWorkSessions, setActiveWorkSessions] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ 
    open: false, 
    title: '', 
    message: '', 
    maintenance: null,
    action: null
  });
  const [selectedMaintenance, setSelectedMaintenance] = useState(null);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [simpleConfirmDialog, setSimpleConfirmDialog] = useState({
    open: false,
    maintenance: null
  });
  
  // Filter states
  const [filters, setFilters] = useState({
    protocolNumber: '',
    customer: '',
    vehicle: '',
    date: '',
    days: '',
    description: '',
    status: '',
    priority: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const fetchActiveWorkSessions = async () => {
      try {
        const res = await api.get('/work-sessions/active');
        if (Array.isArray(res.data)) {
          logger.debug('✅ Sessioni di lavoro attive caricate:', res.data.length);
        setActiveWorkSessions(res.data);
        } else {
          logger.error('❌ Formato dati non valido');
          setActiveWorkSessions([]);
        }
      } catch (err) {
        logger.error('❌ Errore nel caricamento delle sessioni attive:', err);
        setActiveWorkSessions([]);
      }
    };

    fetchActiveWorkSessions();
    const intervalId = setInterval(fetchActiveWorkSessions, 30000);
    return () => clearInterval(intervalId);
  }, []);

  const hasActiveWorkSession = (maintenanceId) => {
    // Verifica che activeWorkSessions sia un array
    if (!Array.isArray(activeWorkSessions)) {
      logger.warn('⚠️ activeWorkSessions non è un array');
      return false;
    }
    
    // Cerca una sessione attiva per questa manutenzione
    const hasActive = activeWorkSessions.some(session => 
      session.maintenanceId === maintenanceId
    );
    
    if (hasActive) {
      logger.debug(`✅ La manutenzione #${maintenanceId} ha una sessione attiva`);
    }
    
    return hasActive;
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMarkAsDelivered = async (maintenance) => {
    try {
      // Recupera i dati aggiornati della manutenzione
      const response = await api.get(`/maintenance/${maintenance.id}`);
      const updatedMaintenance = response.data;
      
      // Calcola il saldo da versare
      const total = parseFloat(updatedMaintenance.finalCost || 0);
      const discount = parseFloat(updatedMaintenance.discount || 0);
      const deposit = parseFloat(updatedMaintenance.deposit || 0);
      const balance = (total - discount) - deposit;
      
      // Se il saldo è 0€ o molto vicino a 0€ (tolleranza per arrotondamenti), mostra dialog semplice
      if (Math.abs(balance) < 0.01) {
        setSimpleConfirmDialog({
          open: true,
          maintenance: updatedMaintenance
        });
      } else {
        // Altrimenti apri il dialog completo per il pagamento
        setSelectedMaintenance(updatedMaintenance);
        setCompleteDialogOpen(true);
      }
    } catch (err) {
      logger.error('Errore nel recupero dei dati della manutenzione:', err);
      setSnackbar({
        open: true,
        message: 'Errore nel recupero dei dati della manutenzione',
        severity: 'error'
      });
    }
  };

  const handleSimpleConfirmDelivery = async () => {
    const { maintenance } = simpleConfirmDialog;
    if (!maintenance) return;

    try {
      // Aggiorna solo lo stato a "Consegnato" senza creare movimenti contabili
      const putRes = await api.put(`/maintenance/${maintenance.id}`, {
        ...maintenance,
        status: 'Consegnato',
        endDate: maintenance.endDate || new Date()
      });

      setSimpleConfirmDialog({ open: false, maintenance: null });
      
      // Aggiorna la lista
      window.dispatchEvent(new CustomEvent('maintenance-updated'));
      
      setSnackbar({
        open: true,
        message: 'Manutenzione contrassegnata come consegnata',
        severity: 'success'
      });

      try {
        await openMaintenanceReviewWhatsApp(putRes.data || { id: maintenance.id, status: 'Consegnato' });
      } catch (e) {
        logger.error('WhatsApp recensioni dopo consegna:', e);
      }
    } catch (err) {
      logger.error('Errore durante la conferma della consegna:', err);
      setSnackbar({
        open: true,
        message: 'Errore durante la conferma della consegna',
        severity: 'error'
      });
    }
  };

  const handleSendWhatsApp = async (maintenance) => {
    if (!maintenance || !maintenance.Customer) return;
    
    try {
      // Recupera le impostazioni dell'azienda per ottenere il termsAndConditions URL
      const companyId = localStorage.getItem('companyId');
      if (!companyId) {
        logger.error('CompanyId non trovato in localStorage');
        return;
      }
      
      // Prima otteniamo le company-settings
      const companySettingsResponse = await api.get('/company-settings', {
        headers: { 'x-company-id': companyId }
      });
      
      // Poi otteniamo le settings generali che contengono termsAndConditions
      const generalSettingsResponse = await api.get('/settings');
      
      const companySettings = companySettingsResponse.data || {};
      const generalSettings = generalSettingsResponse.data || {};
      
      logger.debug('🔄 Impostazioni recuperate');
      
      // Stati senza messaggio: apri WhatsApp senza testo
      if (maintenance.status === 'In Corso' || maintenance.status === 'In Coda' || maintenance.status === 'Attesa Ricambi') {
        const phoneNumber = maintenance.Customer.phone;
        if (phoneNumber) {
          openWhatsAppChat(phoneNumber, maintenance.Customer.countryCode);
        }
        return;
      }
      
      // Calcola daysInShop
      const daysInShop = calculateDaysInShop(maintenance);
      
      // Calcola il totale considerando lo sconto
      const finalCost = parseFloat(maintenance.finalCost || 0);
      const discount = parseFloat(maintenance.discount || 0);
      const totalAmountWithDiscount = finalCost - discount;
      
      // Template data comune per tutti i tipi di messaggio
      const templateData = {
        customerName: maintenance.Customer.firstName,
        protocolNumber: maintenance.protocolNumber,
        vehicleModel: maintenance.Vehicle ? maintenance.Vehicle.model : '',
        problemDescription: maintenance.description,
        totalAmount: totalAmountWithDiscount > 0 ? totalAmountWithDiscount.toString() : (maintenance.estimatedCost || ''),
        estimateAmount: maintenance.estimatedCost || '',
        deposit: maintenance.deposit || '0',
        // Ottieni il link ai termini dalle impostazioni generali
        termsLink: generalSettings.termsAndConditions || 'https://www.officinabici.it/termini-e-condizioni',
        daysInShop: daysInShop.toString()
      };
      
      // Stato "Da Chiamare": invia messaggio standard "completamento" e poi conferma per passare a "Da Ritirare"
      if (maintenance.status === 'Da Chiamare') {
        const phoneNumber = maintenance.Customer.phone;
        await openWhatsAppWithTemplate(phoneNumber, TEMPLATE_TYPES.MAINTENANCE_COMPLETION, templateData, maintenance.Customer.countryCode);

        setConfirmDialog({
          open: true,
          title: 'Conferma invio messaggio',
          message: 'Il messaggio è stato inviato correttamente?',
          maintenance: maintenance,
          action: 'updateToDaRitirare'
        });
        return;
      }
      
      // For 'Da Ritirare' status, update to 'Sollecitato' after sending reminder
      if (maintenance.status === 'Da Ritirare') {
        const phoneNumber = maintenance.Customer.phone;
        await openWhatsAppWithTemplate(phoneNumber, TEMPLATE_TYPES.PICKUP_REMINDER, templateData, maintenance.Customer.countryCode);
        
        // Show confirmation dialog
        setConfirmDialog({
          open: true,
          title: 'Conferma invio messaggio',
          message: 'Il messaggio è stato inviato correttamente?',
          maintenance: maintenance,
          action: 'updateToSollecitato'
        });
        
        return;
      }
      
      // For other statuses, use template-based messaging
      logger.debug('Dati template WhatsApp preparati');
      
      const templateType = statusToTemplateType[maintenance.status] || TEMPLATE_TYPES.MAINTENANCE_COMPLETION;
      const phoneNumber = maintenance.Customer.phone;

      await openWhatsAppWithTemplate(phoneNumber, templateType, templateData, maintenance.Customer.countryCode);
    } catch (error) {
      logger.error('Errore durante l\'invio del messaggio WhatsApp:', error);
      setSnackbar({
        open: true,
        message: 'Errore durante l\'invio del messaggio WhatsApp',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPage(0); // Reset to first page when filter changes
  };
  
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };
  
  const handleConfirmDialogClose = (confirmed) => {
    const { maintenance, action } = confirmDialog;
    
    if (confirmed && maintenance) {
      if (action === 'updateToDaRitirare') {
        // Update status to 'Da Ritirare'
        api.put(`/maintenance/${maintenance.id}`, { 
          status: 'Da Ritirare',
          description: maintenance.description // Include the existing description
        })
        .then(() => {
          // Dispatch a custom event to trigger a refresh of the maintenance list
          window.dispatchEvent(new CustomEvent('maintenance-updated'));
          setSnackbar({
            open: true,
            message: 'Stato aggiornato a Da Ritirare',
            severity: 'success'
          });
        })
        .catch(err => {
          logger.error('Error updating maintenance status:', err);
          setSnackbar({
            open: true,
            message: 'Errore durante l\'aggiornamento dello stato',
            severity: 'error'
          });
        });
      } else if (action === 'updateToSollecitato') {
        // Update status to 'Sollecitato'
        api.put(`/maintenance/${maintenance.id}`, { 
          status: 'Sollecitato',
          description: maintenance.description // Include the existing description
        })
        .then(() => {
          // Dispatch a custom event to trigger a refresh of the maintenance list
          window.dispatchEvent(new CustomEvent('maintenance-updated'));
          setSnackbar({
            open: true,
            message: 'Stato aggiornato a Sollecitato',
            severity: 'success'
          });
        })
        .catch(err => {
          logger.error('Error updating maintenance status:', err);
          setSnackbar({
            open: true,
            message: 'Errore durante l\'aggiornamento dello stato',
            severity: 'error'
          });
        });
      }
    }
    
    // Close the dialog
    setConfirmDialog({
      ...confirmDialog,
      open: false,
      maintenance: null,
      action: null
    });
  };

  const calculateDaysInShop = (maintenance) => {
    try {
      if (!maintenance) {
        return 0;
      }
      
      // Per i messaggi di sollecito (status "Da Ritirare" o "Sollecitato"),
      // usa la data del primo messaggio di ritiro se disponibile
      if ((maintenance.status === 'Da Ritirare' || maintenance.status === 'Sollecitato') && maintenance.firstPickupReminderSentAt) {
        const firstReminderDate = new Date(maintenance.firstPickupReminderSentAt);
        const now = new Date();
        
        if (isNaN(firstReminderDate.getTime()) || isNaN(now.getTime())) {
          logger.error('Date non valide per il calcolo dei giorni dal primo sollecito');
          return 0;
        }
        
        const differenceInTime = now.getTime() - firstReminderDate.getTime();
        const differenceInDays = Math.ceil(differenceInTime / (1000 * 3600 * 24));
        
        return Math.max(0, differenceInDays);
      }
      
      // Per gli altri casi, usa la data di ingresso (startDate)
      if (!maintenance.startDate) {
        return 0;
      }
      
      const startDate = new Date(maintenance.startDate);
      const endDate = maintenance.endDate ? new Date(maintenance.endDate) : new Date();
      
      // Verifica che le date siano valide
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        logger.error('Date non valide per il calcolo dei giorni');
        return 0;
      }
      
      const differenceInTime = endDate.getTime() - startDate.getTime();
      const differenceInDays = Math.ceil(differenceInTime / (1000 * 3600 * 24));
      
      // Garantisci che il risultato sia almeno 0
      return Math.max(0, differenceInDays);
    } catch (error) {
      logger.error('Errore nel calcolo dei giorni per manutenzione:', error);
      return 0;
    }
  };

  const getStatusColor = (status) => {
    return statusColors[status] || 'default';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Apply filters to maintenances
  const filteredMaintenances = maintenances ? maintenances.filter(maintenance => {
    // Prima filtra in base al tipo di tabella
    if (type === 'in_corso') {
      // Per la tabella "in corso" escludiamo le manutenzioni Consegnate, Da Ritirare e Sollecitate
      if (['Consegnato', 'Da Ritirare', 'Sollecitato'].includes(maintenance.status)) {
        return false;
      }
    } else if (type === 'da_ritirare') {
      // Per la tabella "da ritirare" mostriamo solo Da Ritirare e Sollecitate
      if (!['Da Ritirare', 'Sollecitato'].includes(maintenance.status)) {
        return false;
      }
    } else if (type === 'consegnate') {
      // Per la tabella "consegnate" mostriamo solo le Consegnate
      if (maintenance.status !== 'Consegnato') {
        return false;
      }
    }

    // Protocol number filter
    if (filters.protocolNumber && !maintenance.protocolNumber.toString().includes(filters.protocolNumber)) {
      return false;
    }
    
    // Customer filter
    if (filters.customer && maintenance.Customer) {
      const customerFullName = `${maintenance.Customer.firstName} ${maintenance.Customer.lastName}`.toLowerCase();
      if (!customerFullName.includes(filters.customer.toLowerCase())) {
        return false;
      }
    }
    
    // Vehicle filter
    if (filters.vehicle && maintenance.Vehicle) {
      const vehicleFullName = `${maintenance.Vehicle.brand} ${maintenance.Vehicle.model}`.toLowerCase();
      if (!vehicleFullName.includes(filters.vehicle.toLowerCase())) {
        return false;
      }
    }
    
    // Date filter - simple string match for now
    if (filters.date) {
      const formattedDate = format(new Date(maintenance.startDate), 'dd/MM/yyyy', { locale: it });
      if (!formattedDate.includes(filters.date)) {
        return false;
      }
    }
    
    // Days filter
    if (filters.days) {
      const days = calculateDaysInShop(maintenance);
      if (!days.toString().includes(filters.days)) {
        return false;
      }
    }
    
    // Description filter
    if (filters.description && !maintenance.description.toLowerCase().includes(filters.description.toLowerCase())) {
      return false;
    }
    
    // Status filter
    if (filters.status && maintenance.status !== filters.status) {
      return false;
    }
    
    // Priority filter
    if (filters.priority && maintenance.priority !== filters.priority) {
      return false;
    }
    
    return true;
  }) : [];
  
  if (!maintenances || maintenances.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1">Nessuna manutenzione trovata</Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ width: '100%', p: 0 }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 2,
        gap: 2
      }}>
        <Typography variant="h5" component="h2">
          {type === 'in_corso' && 'Manutenzioni in corso'}
          {type === 'da_ritirare' && 'Manutenzioni da ritirare'}
          {type === 'consegnate' && 'Manutenzioni consegnate'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={toggleFilters}>
            <FilterIcon />
          </IconButton>
        </Box>
      </Box>

      {showFilters && (
        <Paper sx={{ mb: 2, p: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '80px' }}>Protocollo</TableCell>
                <TableCell sx={{ width: '150px' }}>Cliente</TableCell>
                <TableCell sx={{ width: '150px' }}>Veicolo</TableCell>
                <TableCell sx={{ width: '90px' }}>Data</TableCell>
                <TableCell sx={{ width: '60px' }}>Giorni</TableCell>
                              <TableCell sx={{ width: '400px' }}>Descrizione</TableCell>
              <TableCell sx={{ width: '100px' }}>Stato</TableCell>
              <TableCell sx={{ width: '100px' }}>Priorità</TableCell>
              <TableCell align="right" sx={{ width: '140px' }}>Azioni</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <TextField
                    size="small"
                    placeholder="Filtra..."
                    value={filters.protocolNumber}
                    onChange={(e) => handleFilterChange('protocolNumber', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    placeholder="Filtra..."
                    value={filters.customer}
                    onChange={(e) => handleFilterChange('customer', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    placeholder="Filtra..."
                    value={filters.vehicle}
                    onChange={(e) => handleFilterChange('vehicle', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    placeholder="Filtra..."
                    value={filters.date}
                    onChange={(e) => handleFilterChange('date', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    placeholder="Filtra..."
                    value={filters.days}
                    onChange={(e) => handleFilterChange('days', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    placeholder="Filtra..."
                    value={filters.description}
                    onChange={(e) => handleFilterChange('description', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <FormControl fullWidth size="small">
                    <Select
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="">Tutti</MenuItem>
                      {Object.keys(statusColors).map(status => (
                        <MenuItem key={status} value={status}>{status}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>
                  <FormControl fullWidth size="small">
                    <Select
                      value={filters.priority}
                      onChange={(e) => handleFilterChange('priority', e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="">Tutti</MenuItem>
                      <MenuItem value="Alta">Alta</MenuItem>
                      <MenuItem value="Normale">Normale</MenuItem>
                      <MenuItem value="Bassa">Bassa</MenuItem>
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
          </Table>
        </Paper>
      )}

      <TableContainer component={Paper} sx={{ width: '100%', overflow: 'auto' }}>
        <Table sx={{ minWidth: 650 }} size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '80px' }}>Protocollo</TableCell>
              <TableCell sx={{ width: '150px' }}>Cliente</TableCell>
              <TableCell sx={{ width: '150px' }}>Veicolo</TableCell>
              <TableCell sx={{ width: '90px' }}>Data</TableCell>
              <TableCell sx={{ width: '60px' }}>Giorni</TableCell>
              <TableCell sx={{ width: '400px' }}>Descrizione</TableCell>
              <TableCell sx={{ width: '100px' }}>Stato</TableCell>
              <TableCell sx={{ width: '100px' }}>Priorità</TableCell>
              <TableCell align="right" sx={{ width: '140px' }}>Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredMaintenances.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body1">Nessuna manutenzione trovata</Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredMaintenances
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((maintenance) => {
                  const hasActiveSession = hasActiveWorkSession(maintenance.id);
                  return (
                    <TableRow 
                      key={`maintenance-row-${maintenance.id || Math.random()}`}
                      sx={{ 
                        bgcolor: hasActiveSession ? 'rgba(255, 99, 71, 0.15)' : 'inherit',
                        '&:hover': {
                          bgcolor: hasActiveSession ? 'rgba(255, 99, 71, 0.25)' : 'rgba(0, 0, 0, 0.04)'
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {hasActiveSession && (
                            <Tooltip title="Sessione di lavoro attiva">
                              <TimerIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
                            </Tooltip>
                          )}
                        <Typography variant="body2" fontWeight="bold">
                          #{maintenance.protocolNumber || ''}
                        </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {maintenance.Customer ? `${maintenance.Customer.firstName} ${maintenance.Customer.lastName}` : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {maintenance.Vehicle ? `${maintenance.Vehicle.brand} ${maintenance.Vehicle.model}` : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {maintenance.startDate ? 
                            (function() {
                              try {
                                return format(new Date(maintenance.startDate), 'dd/MM/yyyy', { locale: it });
                              } catch (error) {
                                logger.error('Errore formattando la data:', error);
                                return 'Data non valida';
                              }
                            })() 
                            : '-'
                          }
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {calculateDaysInShop(maintenance)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: '400px', wordWrap: 'break-word', overflow: 'hidden' }}>
                        <Typography variant="body2" sx={{ 
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                          lineHeight: 1.2
                        }}>
                          {maintenance.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={maintenance.status}
                          color={getStatusColor(maintenance.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={maintenance.priority}
                          color={getPriorityColor(maintenance.priority)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', alignItems: 'center' }}>
                          {maintenance.hasUnreadNote && (
                            <MaintenanceNoteBell maintenance={maintenance} />
                          )}
                          <Tooltip title="Visualizza dettagli">
                            <IconButton
                              size="medium"
                              onClick={() => onViewDetails(maintenance.id)}
                            >
                              <VisibilityIcon fontSize="medium" />
                            </IconButton>
                          </Tooltip>
                          {maintenance.status !== 'Da Ritirare' && maintenance.status !== 'Sollecitato' && maintenance.status !== 'Consegnato' && (
                            <Tooltip title="Avvia lavorazione">
                              <IconButton
                                size="medium"
                                onClick={() => onStartRepair(maintenance.id)}
                                color={hasActiveSession ? 'primary' : 'default'}
                              >
                                {hasActiveSession ? <TimerIcon fontSize="medium" /> : <BuildIcon fontSize="medium" />}
                              </IconButton>
                            </Tooltip>
                          )}
                          {maintenance.Customer && maintenance.Customer.phone && (
                            <Tooltip title={maintenance.status === 'Da Ritirare' ? "Messaggio di sollecito" : "Invia WhatsApp"}>
                              <IconButton
                                size="medium"
                                onClick={() => handleSendWhatsApp(maintenance)}
                              >
                                <WhatsAppIcon fontSize="medium" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {(maintenance.status === 'Da Ritirare' || maintenance.status === 'Sollecitato') && (
                            <Tooltip title="Contrassegna come consegnato">
                              <IconButton
                                size="medium"
                                onClick={() => handleMarkAsDelivered(maintenance)}
                                color="success"
                              >
                                <CheckIcon fontSize="medium" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={maintenances.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => handleConfirmDialogClose(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleConfirmDialogClose(false)}>No</Button>
          <Button onClick={() => handleConfirmDialogClose(true)} autoFocus>Sì</Button>
        </DialogActions>
      </Dialog>

      <CompleteMaintenanceDialog
        open={completeDialogOpen}
        onClose={() => setCompleteDialogOpen(false)}
        maintenance={selectedMaintenance}
        onCompleted={() => {
          setCompleteDialogOpen(false);
          window.dispatchEvent(new CustomEvent('maintenance-updated'));
        }}
      />

      {/* Dialog semplice per conferma consegna quando importo è 0€ */}
      <Dialog
        open={simpleConfirmDialog.open}
        onClose={() => setSimpleConfirmDialog({ open: false, maintenance: null })}
        aria-labelledby="simple-confirm-dialog-title"
        aria-describedby="simple-confirm-dialog-description"
      >
        <DialogTitle id="simple-confirm-dialog-title">
          Conferma Consegna
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="simple-confirm-dialog-description">
            L'importo da pagare è €0,00. Vuoi confermare la consegna della manutenzione #{simpleConfirmDialog.maintenance?.protocolNumber}?
            <br />
            <strong>Nota:</strong> Non verrà creato alcun movimento contabile.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSimpleConfirmDialog({ open: false, maintenance: null })}>
            Annulla
          </Button>
          <Button onClick={handleSimpleConfirmDelivery} variant="contained" color="primary" autoFocus>
            Conferma Consegna
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MaintenanceList;
